// GenToken/ExternalWalletTransactionHandler.js
import React, { useState, useEffect } from 'react';
import { Connection, PublicKey, Transaction, SystemProgram, LAMPORTS_PER_SOL } from '@solana/web3.js';
import { createMint, getOrCreateAssociatedTokenAccount, mintTo, TOKEN_PROGRAM_ID } from '@solana/spl-token';
import base58 from 'bs58';
import './ExternalWalletTransactionHandler.css';

const ExternalWalletTransactionHandler = ({ 
  pendingTokenData, 
  onSuccess, 
  onError, 
  onClose,
  language 
}) => {
  const [status, setStatus] = useState('pending'); // pending, connecting, processing, success, error
  const [error, setError] = useState(null);
  const [txSignature, setTxSignature] = useState(null);
  
  // 트랜잭션 실행 함수
  const executeTransaction = async () => {
    try {
      setStatus('connecting');
      
      // Phantom 또는 Solflare 지갑이 설치되어 있는지 확인
      if (!window.solana || !window.solana.isPhantom) {
        // Solflare도 확인
        if (!window.solflare) {
          throw new Error(language === 'KR' 
            ? '호환되는 Solana 지갑이 발견되지 않았습니다. Phantom 또는 Solflare 지갑을 설치해주세요.' 
            : 'No compatible Solana wallet found. Please install Phantom or Solflare wallet.');
        }
      }
      
      // 지갑 API 결정 (Phantom 또는 Solflare)
      const wallet = window.solana?.isPhantom ? window.solana : window.solflare;
      
      // 지갑 연결 확인
      if (!wallet.isConnected) {
        await wallet.connect();
        const resp = await wallet.connect();
        if (resp.publicKey) {
          console.log('지갑 연결 성공:', resp.publicKey.toString());
        }
      }
      
      // 연결된 지갑 주소가 지정된 지갑 주소와 일치하는지 확인
      if (wallet.publicKey.toString() !== pendingTokenData.walletAddress) {
        throw new Error(language === 'KR'
          ? `토큰을 생성하려면 등록된 지갑(${pendingTokenData.walletAddress})에 연결해야 합니다. 현재 연결된 지갑: ${wallet.publicKey.toString()}`
          : `You need to connect to your registered wallet (${pendingTokenData.walletAddress}) to create the token. Currently connected: ${wallet.publicKey.toString()}`);
      }
      
      setStatus('processing');
      
      // 1. Solana 연결 생성
      const connection = new Connection(
        pendingTokenData.instructions.network === 'mainnet-beta'
          ? 'https://api.mainnet-beta.solana.com'
          : pendingTokenData.instructions.network === 'testnet'
            ? 'https://api.testnet.solana.com'
            : 'https://api.devnet.solana.com',
        'confirmed'
      );
      
      // 2. 민트 키페어 복원
      const mintKeypairSecret = Buffer.from(pendingTokenData.instructions.mintKeypair, 'base64');
      const mintPubkey = new PublicKey(pendingTokenData.tokenAddress);
      
      // 3. 민트 계정 생성 트랜잭션 구성
      const mintRent = await connection.getMinimumBalanceForRentExemption(82);
      
      // 트랜잭션 생성
      const transaction = new Transaction();
      
      // 민트 계정 생성
      transaction.add(
        SystemProgram.createAccount({
          fromPubkey: wallet.publicKey,
          newAccountPubkey: mintPubkey,
          lamports: mintRent,
          space: 82,
          programId: TOKEN_PROGRAM_ID
        })
      );
      
      // 4. 민트 초기화 및 토큰 발행 명령 추가 (클라이언트에서 처리 불가)
      // 대신 서버에 요청해서 트랜잭션을 완성해야 함
      
      // 현재 블록해시 가져오기
      const { blockhash } = await connection.getRecentBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = wallet.publicKey;
      
      // 5. 서명 요청
      const signedTransaction = await wallet.signTransaction(transaction);
      
      // 6. 트랜잭션 전송
      const signature = await connection.sendRawTransaction(
        signedTransaction.serialize()
      );
      
      // 7. 트랜잭션 확인
      await connection.confirmTransaction(signature);
      
      // 8. 서버에 완료 알림 (새로운 API 엔드포인트 필요)
      const completeResponse = await fetch(`/api/solana/token/complete-creation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tokenAddress: pendingTokenData.tokenAddress,
          walletAddress: wallet.publicKey.toString(),
          signature: signature
        })
      });
      
      if (!completeResponse.ok) {
        throw new Error('Failed to complete token creation process');
      }
      
      // 성공!
      setTxSignature(signature);
      setStatus('success');
      
      // 부모 컴포넌트에 성공 알림
      if (onSuccess) {
        onSuccess({
          tokenAddress: pendingTokenData.tokenAddress,
          tokenName: pendingTokenData.tokenName,
          tokenSymbol: pendingTokenData.tokenSymbol,
          signature: signature
        });
      }
      
    } catch (error) {
      console.error('Transaction error:', error);
      setError(error.message);
      setStatus('error');
      
      if (onError) {
        onError(error.message);
      }
    }
  };
  
  // 컴포넌트가 마운트되면 자동으로 트랜잭션 실행 시작
  useEffect(() => {
    if (pendingTokenData) {
      executeTransaction();
    }
  }, [pendingTokenData]);
  
  return (
    <div className="external-wallet-handler">
      <div className="wallet-handler-header">
        <h3>
          {language === 'KR' 
            ? '외부 지갑 트랜잭션' 
            : 'External Wallet Transaction'}
        </h3>
        {status !== 'processing' && (
          <button className="close-button" onClick={onClose}>×</button>
        )}
      </div>
      
      <div className="wallet-handler-content">
        {status === 'pending' && (
          <div className="wallet-status pending">
            <div className="spinner"></div>
            <p>
              {language === 'KR' 
                ? '지갑 연결 준비 중...' 
                : 'Preparing to connect to wallet...'}
            </p>
          </div>
        )}
        
        {status === 'connecting' && (
          <div className="wallet-status connecting">
            <div className="spinner"></div>
            <p>
              {language === 'KR' 
                ? '지갑 연결 중...' 
                : 'Connecting to wallet...'}
            </p>
            <p className="subtext">
              {language === 'KR' 
                ? '지갑 앱에서 연결 요청을 확인해주세요.' 
                : 'Please check your wallet app for connection request.'}
            </p>
          </div>
        )}
        
        {status === 'processing' && (
          <div className="wallet-status processing">
            <div className="spinner"></div>
            <p>
              {language === 'KR' 
                ? '트랜잭션 처리 중...' 
                : 'Processing transaction...'}
            </p>
            <p className="subtext">
              {language === 'KR' 
                ? '지갑 앱에서 트랜잭션 서명 요청을 확인해주세요.' 
                : 'Please check your wallet app for transaction signing request.'}
            </p>
            <div className="token-info">
              <p>
                {language === 'KR' ? '토큰 이름' : 'Token Name'}: 
                <span>{pendingTokenData.tokenName}</span>
              </p>
              <p>
                {language === 'KR' ? '토큰 심볼' : 'Token Symbol'}: 
                <span>{pendingTokenData.tokenSymbol}</span>
              </p>
            </div>
          </div>
        )}
        
        {status === 'success' && (
          <div className="wallet-status success">
            <div className="success-icon">✓</div>
            <p>
              {language === 'KR' 
                ? '토큰 생성 성공!' 
                : 'Token Created Successfully!'}
            </p>
            <div className="token-info">
              <p>
                {language === 'KR' ? '토큰 주소' : 'Token Address'}: 
                <span>{pendingTokenData.tokenAddress}</span>
              </p>
              <p>
                {language === 'KR' ? '트랜잭션' : 'Transaction'}: 
                <a 
                  href={`https://explorer.solana.com/tx/${txSignature}?cluster=${pendingTokenData.instructions.network}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  {txSignature.slice(0, 8)}...{txSignature.slice(-8)}
                </a>
              </p>
            </div>
            <button 
              className="close-success-button"
              onClick={onClose}
            >
              {language === 'KR' ? '완료' : 'Done'}
            </button>
          </div>
        )}
        
        {status === 'error' && (
          <div className="wallet-status error">
            <div className="error-icon">!</div>
            <p>
              {language === 'KR' 
                ? '오류가 발생했습니다' 
                : 'An error occurred'}
            </p>
            <p className="error-message">{error}</p>
            <div className="error-actions">
              <button 
                className="retry-button"
                onClick={executeTransaction}
              >
                {language === 'KR' ? '다시 시도' : 'Retry'}
              </button>
              <button 
                className="cancel-button"
                onClick={onClose}
              >
                {language === 'KR' ? '취소' : 'Cancel'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExternalWalletTransactionHandler;